'use client'

import { FormBlock as FormBlockType, Form } from '@/payload-types'

import RichText from '../components/RichText'
import { useRouter } from 'next/navigation'
import { useTranslations } from 'next-intl'
import React, { Suspense, useCallback, useRef, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Button } from '../components/ui/Button'

import { getClientSideURL } from '@onclicks/seo/getURL'
import { fields } from '../components/FieldForm/fields'
import { PrefillFromSearchParams } from '../components/FieldForm/PrefillFromSearchParams'
import {
  PayloadRecaptcha,
  type RecaptchaHandle,
} from '@onclicks/recaptcha/components/PayloadRecaptcha'

export const FormBlock: React.FC<FormBlockType> = (props) => {
  const { enableIntro, form: formFromProps, introContent } = props
  const t = useTranslations('web_contact')

  const form =
    typeof formFromProps === 'object' && formFromProps !== null ? (formFromProps as Form) : null

  const formID = form?.id
  const confirmationMessage = form?.confirmationMessage
  const confirmationType = form?.confirmationType
  const redirect = form?.redirect
  const submitButtonLabel = form?.submitButtonLabel
  const requireRecaptcha = form?.requireRecaptcha

  const recaptcha = useRef<RecaptchaHandle>(null)

  const formMethods = useForm({
    defaultValues: form?.fields as unknown as Record<string, string>,
  })
  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
  } = formMethods

  const [isLoading, setIsLoading] = useState(false)
  const [hasSubmitted, setHasSubmitted] = useState<boolean>()
  const [error, setError] = useState<{ message: string; status?: string } | undefined>()
  const router = useRouter()

  const onSubmit = useCallback(
    (data: Record<string, string>, captcha: RecaptchaHandle | null) => {
      let loadingTimerID: ReturnType<typeof setTimeout>
      if (!formID) return

      const submitForm = async () => {
        setError(undefined)

        const captchaValue = captcha?.getValue() ?? undefined

        if (requireRecaptcha && !captchaValue) {
          setError({ message: t('captcha_required') })
          return
        }

        const dataToSend = Object.entries(data).map(([name, value]) => ({
          field: name,
          value,
        }))

        // delay loading indicator by 1s
        loadingTimerID = setTimeout(() => {
          setIsLoading(true)
        }, 1000)

        try {
          const req = await fetch(`${getClientSideURL()}/api/form-submissions`, {
            body: JSON.stringify({
              form: formID,
              submissionData: dataToSend,
              recaptcha: captchaValue,
            }),
            headers: {
              'Content-Type': 'application/json',
            },
            method: 'POST',
          })

          const res = await req.json()

          clearTimeout(loadingTimerID)

          if (req.status >= 400) {
            setIsLoading(false)
            setError({ message: res.errors?.[0]?.message || t('form_error') })
            captcha?.reset()
            return
          }

          setIsLoading(false)
          setHasSubmitted(true)
          captcha?.reset()

          if (confirmationType === 'redirect' && redirect?.url) {
            router.push(redirect.url)
          }
        } catch (err) {
          console.warn(err)
          clearTimeout(loadingTimerID)
          setIsLoading(false)
          setError({ message: t('form_error') })
          captcha?.reset()
        }
      }

      void submitForm()
    },
    [router, formID, redirect, confirmationType, requireRecaptcha, t],
  )

  if (!form) return null

  return (
    <div className="flex h-full flex-col rounded-lg bg-linear-to-b from-[#2A2D3F] via-[#0A0A0E] to-black p-8 text-white shadow-2xl">
      {enableIntro && introContent && !hasSubmitted && (
        <>
          <RichText
            className="[&_h2]:font-bold [&_h2]:underline [&_h2]:underline-offset-8 [&_h2]:decoration-primary"
            data={introContent}
            enableGutter={false}
            enableProse={false}
          />
          <div className="w-full h-1 bg-primary mt-2 mb-8 " />
        </>
      )}

      <FormProvider {...formMethods}>
        {/* useSearchParams exige une Suspense sur une page prérendue : isolée ici pour ne pas suspendre tout le formulaire */}
        <Suspense fallback={null}>
          <PrefillFromSearchParams fields={form.fields} />
        </Suspense>
        {!isLoading && hasSubmitted && confirmationType === 'message' && confirmationMessage && (
          <RichText data={confirmationMessage} enableGutter={false} />
        )}
        {isLoading && !hasSubmitted && <p>{t('form_loading')}</p>}
        {!isLoading && error && (
          <p role="alert" className="mb-4 rounded border border-red-300 bg-red-50 p-3 text-red-700">
            {error.message}
          </p>
        )}
        {!hasSubmitted && (
          <form
            id={formID?.toString()}
            onSubmit={(event) => {
              // Le ref du captcha n'est lu qu'au moment de la soumission, jamais pendant le rendu
              const captcha = recaptcha.current
              void handleSubmit((data) => onSubmit(data, captcha))(event)
            }}
          >
            <div className="mb-4 flex flex-wrap gap-x-6 gap-y-4 last:mb-0">
              {form.fields?.map((field, index) => {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const Field: React.FC<any> = fields?.[field.blockType as keyof typeof fields]
                if (!Field) return null

                const widthValue = 'width' in field ? field.width : null
                const isFullWidth = !widthValue || widthValue >= 100

                return (
                  <div
                    key={index}
                    className="w-full md:w-(--field-width) shrink-0 grow-0"
                    style={
                      {
                        '--field-width': isFullWidth ? '100%' : `calc(${widthValue}% - 0.75rem)`,
                      } as React.CSSProperties
                    }
                  >
                    <Field
                      form={form}
                      {...field}
                      {...formMethods}
                      control={control}
                      errors={errors}
                      register={register}
                    />
                  </div>
                )
              })}
            </div>

            <div className="mt-6 flex flex-col items-end gap-6">
              <PayloadRecaptcha ref={recaptcha} requireRecaptcha={requireRecaptcha} />

              <Button form={formID?.toString()} type="submit" variant="default" className="px-10">
                {submitButtonLabel}
              </Button>
            </div>
          </form>
        )}
      </FormProvider>
    </div>
  )
}
